import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';
import { AnswerCodeInput, QuestionSourceType } from 'stores/TrainingAdminStoreAdapter';

// 受講画面で source_type を表示するため再エクスポートする（定義元は TrainingAdminStoreAdapter）
export { QuestionSourceType } from 'stores/TrainingAdminStoreAdapter';

// --- 型定義（受講） ---

/** 割当課題一覧の1要素（API6351・進捗サマリ付き） */
export type LearnAssignmentResult = {
  assignmentId: string;
  name: string;
  description: string;
  /** クリア済み設問数（いずれか1言語クリアで設問クリア） */
  clearedQuestionCount: number;
  /** 課題内の全設問数 */
  totalQuestionCount: number;
};

/** 設問の言語別クリア状況（一覧・API6352） */
export type QuestionLanguageStatus = {
  extension: string;
  cleared: boolean;
};

/** 課題内の設問一覧の1要素（API6352・自分のクリア状況付き） */
export type LearnQuestionSummary = {
  questionId: string;
  title: string;
  sourceType: QuestionSourceType;
  /** 言語別クリア状況 */
  languages: QuestionLanguageStatus[];
  /** いずれか1言語をクリア済みか（設問クリア） */
  cleared: boolean;
};

/** 設問取得の言語別（API6353） */
export type LearnQuestionLanguage = {
  extension: string;
  cleared: boolean;
  /** クリア済みなら正解コード本文 / 削除済み・未クリアは null（API6353） */
  answerCode: string | null;
};

/** 設問取得（受講・API6353） */
export type LearnQuestionDetail = {
  questionId: string;
  title: string;
  sourceType: QuestionSourceType;
  /** 本文（FORM=md / KNZ=knz ソース）。source_type で出し分ける */
  content: string | null;
  /** 言語別クリア状況＋クリア済み言語の正解コード */
  languages: LearnQuestionLanguage[];
};

/** 解答検証の期待値ごと結果（API6354） */
export type VerifyResultItem = {
  /** 実行時引数（引数なしは null） */
  arguments: string | null;
  expectedStdout: string;
  actualStdout: string;
  /** judge0 status.id */
  statusId: number;
  statusDescription: string;
  /** statusId==3(Accepted) かつ 末尾トリム完全一致で true */
  ok: boolean;
};

/** 解答検証レスポンス（API6354） */
export type VerifyAnswerResult = {
  /** 全 ok=true で true（＝当該言語クリア確定） */
  cleared: boolean;
  /** 検証した言語（解答ファイルの拡張子） */
  extension: string;
  /** 期待値ごとの結果（sort_order 順・0件はありえない） */
  results: VerifyResultItem[];
};

/** 設問反映（API6316）リクエスト。フロントが knz をパースした構造化データを渡す。 */
export type SyncQuestionParams = {
  /** knz ファイルの fileId（必須） */
  sourceFileId: string;
  /** タイトル（必須・knz FrontMatter から抽出） */
  title: string;
  /** 正解コード（必須・1件以上。knz の AnswerCode/Expected をパース） */
  answerCodes: AnswerCodeInput[];
};

// --- 共通 ---

const sessionHeaders = () => ({
  headers: {
    'X-Session-Id': localStorage.getItem('session-id'),
  },
});

// --- API関数（設問反映・管理⇔workspace） ---

/**
 * 設問反映API（API6316 / DEVELOPER）
 * KNZ設問について、フロントが knz をパースした構造化データで設問を同期する。
 * 保存（API6107・純粋保存）とは分離した明示操作。workspace の `[設問に反映]` から呼ぶ。
 * バックエンドは knz をパースしない。
 * @param params.sourceFileId - knz ファイルの fileId（必須）
 * @param params.title - タイトル（必須・knz から抽出, 最大128文字）
 * @param params.answerCodes - 正解コード（必須, 1件以上。各正解コードに期待値1件以上）
 * @returns 対象の questionId
 */
export const syncQuestion = async (params: SyncQuestionParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.SYNC_TRAINING_QUESTION,
      params,
      sessionHeaders(),
    );
    return response.data.questionId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

// --- API関数（受講） ---

/**
 * 割当課題一覧取得API（API6351 / 認証のみ）
 * 自分に割り当てられた課題一覧を進捗サマリ付きで取得する。
 * 非表示課題も割当済みなら表示される。
 */
export const listLearnAssignment = async (): Promise<LearnAssignmentResult[]> => {
  try {
    const response = await axios.post(
      KanzhongBackend.LIST_TRAINING_LEARN_ASSIGNMENT,
      {},
      sessionHeaders(),
    );
    return response.data.results ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 課題設問一覧取得API（API6352 / 認証のみ）
 * 課題内の設問一覧を自分のクリア状況付きで取得する。割当外は404。
 * @param assignmentId - 課題ID（必須）
 */
export const listLearnQuestions = async (assignmentId: string): Promise<LearnQuestionSummary[]> => {
  try {
    const response = await axios.post(
      KanzhongBackend.LIST_TRAINING_LEARN_QUESTIONS,
      { assignmentId },
      sessionHeaders(),
    );
    // API6352 は設問レベルの cleared を返さず languages[].cleared のみ返す。
    // 設問クリア = いずれか1言語をクリア（D8-C）なので languages から導出する。
    return (response.data.results ?? []).map((q: LearnQuestionSummary): LearnQuestionSummary => {
      const languages = q.languages ?? [];
      return { ...q, languages, cleared: languages.some((l) => l.cleared) };
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 設問取得API（受講・API6353 / 認証のみ）
 * 本文・言語別クリア状況・クリア済み言語の正解コードを取得する。割当外は404。
 * @param questionId - 設問ID（必須）
 */
export const getLearnQuestion = async (questionId: string): Promise<LearnQuestionDetail> => {
  try {
    const response = await axios.post(
      KanzhongBackend.GET_TRAINING_LEARN_QUESTION,
      { questionId },
      sessionHeaders(),
    );
    const raw = response.data.result ?? response.data;
    return { ...raw, languages: raw.languages ?? [] };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 解答検証API（API6354 / 認証のみ）★コア
 * 解答ファイルを設問の全期待値で judge0 検証し、全OKならクリア判定する。
 * 言語はリクエストで受けず解答ファイルの拡張子で決まる。他人ファイルは422。
 * @param questionId - 設問ID（必須）
 * @param answerFileId - 解答ファイルのID（必須・自分の workspace のファイル）
 */
export const verifyAnswer = async (
  questionId: string,
  answerFileId: string,
): Promise<VerifyAnswerResult> => {
  try {
    const response = await axios.post(
      KanzhongBackend.VERIFY_TRAINING_ANSWER,
      { questionId, answerFileId },
      sessionHeaders(),
    );
    return response.data.result ?? response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
